// UNFINISHED BOOKINGS: what Book & pay saves on the server, and when.
// Pure, so node --test runs it (booking-capture.test.mjs).
//
// Robin, 17 Oct 2026: "whats annoying is someone trying to book and bouncing
// literally its criminal..agree on mobile and email save". Until then the
// email and mobile stayed in the browser until payment, so whoever left
// before paying was anonymous (Teresa typed her mobile at 11:53 on 17 Oct
// and nothing was saved). Now, once a valid email or UK mobile is in a box
// and the customer leaves it, the booking as it stands goes to
// POST /api/v2/booking_drafts (BookingDraft), one row per browser session.
// The team sees it after about 15 minutes unpaid; the customer gets ONE
// reminder after about an hour, with a link (/booking?resume=<token>) that
// puts the booking back in the form (restoreToSaved).
import { isValidUkMobile, normaliseUkMobile } from './mobile.js'
import { cleanEmail, emailRuleFailed } from './payer-email.js'
import { postcodeDistrict } from './postcode.js'

export const DRAFT_TOKEN_KEY = 'gwmc_booking_draft_token'

// A valid email, cleaned as the card path cleans it, or null.
export const validEmail = raw => {
  const email = cleanEmail(raw)
  if (!email || emailRuleFailed(email)) return null
  if (/@guest\.gowashmycar\.com$/i.test(email)) return null
  return email
}

// A valid UK mobile, as the form shows it, or null.
export const validMobile = raw => {
  const typed = String(raw || '').trim()
  return typed && isValidUkMobile(typed) ? normaliseUkMobile(typed) : null
}

// A random id for this browser session (one draft per session). `storage`
// is sessionStorage; `random` is injectable for tests.
export const draftToken = (storage, random = randomToken) => {
  try {
    const known = storage && storage.getItem(DRAFT_TOKEN_KEY)
    if (known && /^[\w-]{16,64}$/.test(known)) return known
    const fresh = random()
    if (storage) storage.setItem(DRAFT_TOKEN_KEY, fresh)
    return fresh
  } catch (e) {
    return random()
  }
}

export const randomToken = () => {
  const bytes = new Uint8Array(18)
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : null
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes)
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256)
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

// "2026-10-18" in the visitor's own calendar, or null.
export const isoDay = value => {
  if (!value) return null
  const d = value instanceof Date ? value : new Date(value)
  if (isNaN(d)) return null
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// The request body, or null when there is nothing to reach anyone by.
// `form` is the booking form's values (formDraft shape: carSize, date,
// timeSlot, postcode, address, notes, mobile); `restore` the form snapshot
// the resume link puts back (form + services + servicesAvailability).
// `whatsappOptin`: the "Send me offers and wash reminders on WhatsApp" box
// (true / false; anything else leaves it out). booking_help_v1 is MARKETING,
// so the reminder goes by WhatsApp only to someone who ticked it or has
// written to us there before (BookingDraft::Reminder).
export const capturePayload = ({ token, email, mobile, firstName, form = {}, wash, extras = [], total, sessionId, distinctId, internal, restore, whatsappOptin }) => {
  const goodEmail = validEmail(email)
  const goodMobile = validMobile(mobile)
  if (!token || (!goodEmail && !goodMobile)) return null
  const context = {
    wash: wash || undefined,
    size: form.carSize || undefined,
    date: isoDay(form.date) || undefined,
    slot: form.timeSlot || undefined,
    extras: extras.length ? extras : undefined,
    postcode_outward: postcodeDistrict(form.postcode) || undefined,
    total: typeof total === 'number' && total > 0 ? total.toFixed(2) : undefined,
    restore: restore || undefined
  }
  Object.keys(context).forEach(k => context[k] === undefined && delete context[k])
  const body = { token, context }
  if (goodEmail) body.email = goodEmail
  if (goodMobile) body.mobile = goodMobile
  if (firstName && String(firstName).trim()) body.first_name = String(firstName).trim()
  if (sessionId) body.posthog_session_id = sessionId
  if (distinctId) body.posthog_distinct_id = distinctId
  if (internal) body.internal = true
  if (typeof whatsappOptin === 'boolean') body.whatsapp_optin = whatsappOptin
  return body
}

// What makes two saves the same save: no request when nothing changed.
export const captureKey = body => (body ? JSON.stringify(body) : '')

// The resume answer as the booking state Book & pay reads on load
// (store/booking-persistence.js, step 'book'), saved now, or null.
export const restoreToSaved = (restore, now = Date.now()) => {
  if (!restore || typeof restore !== 'object' || !restore.form || typeof restore.form !== 'object') return null
  const out = { step: 'book', form: restore.form, redirectedAt: null, savedAt: now }
  if (restore.services && restore.servicesAvailability) {
    out.services = restore.services
    out.servicesAvailability = restore.servicesAvailability
  }
  return out
}

// The resume token from a URL's search string, or null.
export const resumeTokenFrom = search => {
  try {
    const value = new URLSearchParams(search || '').get('resume')
    return value && value.length <= 1024 ? value : null
  } catch (e) {
    return null
  }
}
